import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Send, CheckCircle, AlertCircle } from 'lucide-react';
import Footer from '../components/Footer';

export default function ContactPage() {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState(null); // {type:'success'|'error', text}

    const submit = async (e) => {
        e.preventDefault();
        setStatus(null);
        if (!name.trim() || !email.trim() || !subject.trim() || message.trim().length < 10) {
            setStatus({ type: 'error', text: 'Please fill in all fields. Message must be at least 10 characters.' });
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post('/contact', {
                name: name.trim(),
                email: email.trim(),
                subject: subject.trim(),
                message: message.trim(),
            });
            setStatus({ type: 'success', text: data.message || 'Thank you — your message has been received.' });
            setName('');
            setEmail('');
            setSubject('');
            setMessage('');
        } catch (err) {
            setStatus({ type: 'error', text: err.response?.data?.detail || 'Something went wrong. Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col" data-testid="contact-page">
            {/* Header */}
            <header className="border-b border-zinc-100 sticky top-0 bg-white z-10">
                <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-zinc-700 hover:text-zinc-900"
                        data-testid="contact-back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="font-heading text-2xl tracking-tight" data-testid="contact-title">Contact Us</h1>
                    <div className="w-5" />
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-4 py-8 w-full flex-1">
                <p className="text-sm text-zinc-600 mb-6 max-w-xl" data-testid="contact-intro">
                    Have a question or need help? Send us a message and our team will get back to you within 1–2 business days.
                </p>

                <form onSubmit={submit} className="space-y-5 max-w-xl" data-testid="contact-form">
                    <div>
                        <Label htmlFor="contact-name" className="text-xs uppercase tracking-wider text-zinc-500">Your name</Label>
                        <Input
                            id="contact-name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            className="mt-1.5 h-12 rounded-none"
                            placeholder="Jane Doe"
                            maxLength={200}
                            data-testid="contact-name-input"
                            required
                        />
                    </div>
                    <div>
                        <Label htmlFor="contact-email" className="text-xs uppercase tracking-wider text-zinc-500">Your email</Label>
                        <Input
                            id="contact-email"
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="mt-1.5 h-12 rounded-none"
                            placeholder="you@example.com"
                            data-testid="contact-email-input"
                            required
                        />
                    </div>
                    <div>
                        <Label htmlFor="contact-subject" className="text-xs uppercase tracking-wider text-zinc-500">Subject</Label>
                        <Input
                            id="contact-subject"
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                            className="mt-1.5 h-12 rounded-none"
                            placeholder="What's this about?"
                            maxLength={200}
                            data-testid="contact-subject-input"
                            required
                        />
                    </div>
                    <div>
                        <Label htmlFor="contact-message" className="text-xs uppercase tracking-wider text-zinc-500">Message</Label>
                        <Textarea
                            id="contact-message"
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            className="mt-1.5 rounded-none min-h-[160px]"
                            placeholder="Please include your registered email, your account type, and a clear description of your issue."
                            maxLength={5000}
                            data-testid="contact-message-input"
                            required
                        />
                        <p className="text-[10px] text-zinc-400 mt-1">{message.length}/5000 (minimum 10)</p>
                    </div>

                    {status && (
                        <div
                            className={`flex items-start gap-2 p-3 border-2 ${status.type === 'success' ? 'border-zinc-900 bg-zinc-50' : 'border-red-300 bg-red-50'}`}
                            data-testid="contact-status"
                        >
                            {status.type === 'success' ? <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-600" />}
                            <p className={`text-sm ${status.type === 'success' ? 'text-zinc-900' : 'text-red-700'}`} data-testid="contact-status-text">{status.text}</p>
                        </div>
                    )}

                    <Button
                        type="submit"
                        disabled={submitting}
                        className="h-12 px-6 rounded-none bg-zinc-900 text-white font-semibold hover:bg-zinc-800"
                        data-testid="contact-submit-btn"
                    >
                        <Send className="w-4 h-4 mr-2" />
                        {submitting ? 'Sending...' : 'Send Message'}
                    </Button>
                </form>
            </div>
            <Footer />
        </div>
    );
}
